import type { TFunction } from "i18next";
import type { ToolCallView } from "../chatStreamReducer";
import { parseSources } from "../messageSources";

/** Tools with their own step wording; any other tool reads "Using …" / "Used …". */
const NAMED_STEPS = [
  "file_search",
  "read_document",
  "list_attachments",
  "read_attachment",
  "view_attachment",
  "web_search",
  "read_web_page",
  "code_interpreter",
  "generate_image",
  "generate_html",
  "generate_dashboard",
  "kg_query",
  "search_sharepoint_files",
  "fetch_sharepoint_document",
  "search_google_drive_files",
  "fetch_google_drive_document",
  "search_outlook_messages",
  "list_outlook_messages",
  "list_calendar_events",
  "send_email",
  "slack_fetch_messages",
  "slack_send_message",
  "jira_search_issues",
  "sql_execute_query",
  "fabric_run_query",
] as const;
type NamedStep = (typeof NAMED_STEPS)[number];
const isNamedStep = (name: string): name is NamedStep => (NAMED_STEPS as readonly string[]).includes(name);

/** "search_sharepoint_sites" → "search sharepoint sites", for a tool without its own wording. */
const readableName = (name: string) => name.replaceAll("_", " ").trim();

/** The search text the model gave the tool, if it took one. */
export function queryOf(tool: Pick<ToolCallView, "arguments">): string | null {
  const args = tool.arguments;
  if (!args || typeof args !== "object") return null;
  const query = (args as Record<string, unknown>).query;
  return typeof query === "string" && query.trim() ? query.trim() : null;
}

/** What a tool step is called while it runs ("Searching knowledge") or once it's done ("Searched knowledge"). */
export function stepName(t: TFunction<"chat">, tool: Pick<ToolCallView, "name">, running: boolean): string {
  const tense = running ? "running" : "done";
  return isNamedStep(tool.name)
    ? t(`progress.steps.${tool.name}.${tense}`)
    : t(`progress.fallback.${tense}`, { tool: readableName(tool.name) });
}

/**
 * The status line: the newest running tool with what it was asked ("Searching knowledge" + ": pricing"), else "Writing
 * the response" once text is coming, else "Thinking".
 */
export function currentStep(
  t: TFunction<"chat">,
  tools: ToolCallView[],
  writing: boolean,
): { label: string; query: string | null } {
  const running = tools.findLast((tool) => tool.status === "running");
  if (running) return { label: stepName(t, running, true), query: queryOf(running) };
  return { label: writing ? t("progress.writing") : t("progress.thinking"), query: null };
}

export interface StepView {
  id: string;
  label: string;
  status: "done" | "error";
  query: string | null;
  /** What it found ("Sales Playbook · 6 passages"). */
  found: string[];
}

/** The finished steps, in order, with what each was asked and found. */
export function finishedSteps(t: TFunction<"chat">, tools: ToolCallView[]): StepView[] {
  return tools
    .filter((tool): tool is ToolCallView & { status: "done" | "error" } => tool.status !== "running")
    .map((tool) => {
      const name = stepName(t, tool, false);
      return {
        id: tool.id,
        label: tool.status === "error" ? t("progress.failed", { step: stepName(t, tool, true) }) : name,
        status: tool.status,
        query: queryOf(tool),
        found: parseSources(tool.sources).map((source) =>
          source.chunks ? t("progress.passages", { name: source.name, count: source.chunks }) : source.name,
        ),
      };
    });
}
